/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react"
import { Td, Tr } from "@lightspeed/cirrus-table"
import { Button } from "@lightspeed/flame/Button"
import { Input } from "@lightspeed/flame/Input"
import { useDispatch, useSelector } from "react-redux"
import * as databaseActions from "../../../redux/actions/databaseActions"
import D365DimensionsModal from "../D365DimensionsModal"
import QuickBooksClassDepartmentField from "../QuickBooksClassDepartmentField"
import {
  formatD365DimensionTooltip,
  getD365DimensionMappingValue,
  hasD365DimensionMapping,
} from "../../../utils/d365Dimensions"

const Row = ({
  item,
  accountsList = [],
  classList = [],
  d365DimensionsList = [],
  customersList = [],
  isD365Integration = false,
  hideClassDepartmentColumn = false,
}) => {
  const dispatch = useDispatch()
  const ui = useSelector((state) => state.ui)
  const userState = useSelector((state) => state.userState)
  const database = useSelector((state) => state.database)

  const [edit, setEdit] = useState(false)
  const [changed, setChanged] = useState(false)

  // GL
  const [inputBox1, setInputBox1] = useState("") // account_id / account
  const [inputBox2, setInputBox2] = useState("") // account_description

  // NEW: Class/Dep/Loc + Customer
  const [inputBox3, setInputBox3] = useState("") // class_department_location
  const [inputBox4, setInputBox4] = useState("") // qb_customer_id
  const [customerId, setCustomerId] = useState("") // interni ID (ako postoji)
  const [inputBox5, setInputBox5] = useState("") // tax_class_allocation
  const [mealMappings, setMealMappings] = useState([])
  const [dimensionMapping, setDimensionMapping] = useState("")

  // normalizacija (podržava oba naming-a: account vs account_id)
  const normalAccount = item?.account_id ?? item?.account ?? ""
  const normalAccountDescription = item?.account_description ?? ""

  const normalClassDeptLoc = item?.class_department_location ?? ""
  const normalCustomer = item?.qb_customer_id ?? ""
  const normalCustomerInternalId = item?.customer_id ? String(item.customer_id) : ""
  const normalTaxClassAllocation =
    item?.tax_class_allocation ?? item?.taxClassAllocation ?? ""
  const normalDimensionMapping = getD365DimensionMappingValue(item)
  const isRetailR = database.currentCompany.type === "RETAIL"
  const mealPeriodSettings = ui.settings || {}
  const activeMealPeriods =
    database.currentCompany.type === "RESTOK" &&
    mealPeriodSettings.meal_period_enabled === "on"
      ? [1, 2, 3, 4]
          .map((index) => ({
            index,
            label: String(mealPeriodSettings[`meal_period_${index}_name`] || "").trim(),
          }))
          .filter((period) => period.label !== "")
      : []
  const isMealPeriodMode = activeMealPeriods.length > 0

  const parseMaybeJson = (value) => {
    if (!value) return null
    if (typeof value === "object") return value

    try {
      return JSON.parse(value)
    } catch (err) {
      return null
    }
  }

  const getExistingMealMapping = (period) => {
    const rawMappings = parseMaybeJson(
      item?.meal_period_mapping || item?.meal_period_mappings || item?.meal_periods
    )
    const mappingList = Array.isArray(rawMappings)
      ? rawMappings
      : Array.isArray(rawMappings?.mappings)
      ? rawMappings.mappings
      : []

    return (
      mappingList.find(
        (mapping) =>
          Number(
            mapping.key ??
              mapping.meal_period_key ??
              mapping.index ??
              mapping.meal_period_index ??
              mapping.period_index
          ) === period.index ||
          mapping.name === period.label ||
          mapping.meal_period_name === period.label
      ) || {}
    )
  }

  const buildMealMappings = () =>
    activeMealPeriods.map((period) => {
      const existing = getExistingMealMapping(period)
      const prefix = `meal_period_${period.index}`

      return {
        key: period.index,
        mealPeriodKey: period.index,
        rowKey: `${item.company_id}-${item.tax_id}-${period.index}`,
        index: period.index,
        name: period.label,
        accountId:
          existing.account_id ??
          existing.account ??
          existing.accountId ??
          item?.[`${prefix}_account_id`] ??
          normalAccount,
        accountDescription:
          existing.account_description ??
          existing.accountDescription ??
          item?.[`${prefix}_account_description`] ??
          normalAccountDescription,
        classDepartmentLocation:
          existing.class_department_location ??
          existing.classDepartmentLocation ??
          item?.[`${prefix}_class_department_location`] ??
          normalClassDeptLoc,
        customerId:
          existing.qb_customer_id ??
          existing.customer_id ??
          existing.customerId ??
          item?.[`${prefix}_qb_customer_id`] ??
          normalCustomer,
      }
    })

  // Dropdown stanja
  const [showDropdownAcc, setShowDropdownAcc] = useState(false)
  const [searchTextAcc, setSearchTextAcc] = useState("")


  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [customerSearchText, setCustomerSearchText] = useState("")
  const [d365Modal, setD365Modal] = useState(null)

  // Refs za fokus
  const accSearchRef = useRef(null)
  const customerSearchRef = useRef(null)

  useEffect(() => {
    if (showDropdownAcc) accSearchRef.current?.focus()
  }, [showDropdownAcc])

  useEffect(() => {
    if (showCustomerDropdown) customerSearchRef.current?.focus()
  }, [showCustomerDropdown])

  // Init
  useEffect(() => {
    setInputBox1(normalAccount)
    setInputBox2(normalAccountDescription)
    setInputBox3(normalClassDeptLoc)
    setInputBox4(normalCustomer)
    setCustomerId(normalCustomerInternalId)
    setInputBox5(normalTaxClassAllocation)
    setMealMappings(buildMealMappings())
    setDimensionMapping(normalDimensionMapping)

    setEdit(false)
  }, [
    normalAccount,
    normalAccountDescription,
    normalClassDeptLoc,
    normalCustomer,
    normalCustomerInternalId,
    normalTaxClassAllocation,
    normalDimensionMapping,
    item.meal_period_mapping,
    item.meal_period_mappings,
    item.meal_periods,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ])

  // Praćenje promjena
  useEffect(() => {
    const noChanges =
      normalAccount === inputBox1 &&
      normalAccountDescription === inputBox2 &&
      normalClassDeptLoc === inputBox3 &&
      normalCustomer === inputBox4 &&
      (!isRetailR || normalTaxClassAllocation === inputBox5)

    const initialMealMappings = buildMealMappings()
    const mealChanges =
      isMealPeriodMode &&
      JSON.stringify(mealMappings) !== JSON.stringify(initialMealMappings)
    const dimensionChanges =
      isD365Integration && normalDimensionMapping !== dimensionMapping

    setChanged(!noChanges || mealChanges || dimensionChanges)
  }, [
    inputBox1,
    inputBox2,
    inputBox3,
    inputBox4,
    inputBox5,
    normalAccount,
    normalAccountDescription,
    normalClassDeptLoc,
    normalCustomer,
    normalTaxClassAllocation,
    normalDimensionMapping,
    dimensionMapping,
    isRetailR,
    isMealPeriodMode,
    mealMappings,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ])

  const updateMealMapping = (periodIndex, changes) => {
    setMealMappings((prev) =>
      prev.map((mapping) =>
        mapping.index === periodIndex ? { ...mapping, ...changes } : mapping
      )
    )
    setChanged(true)
  }

  const getMealMappingsPayload = () =>
    mealMappings.map((mapping) => ({
      key: mapping.mealPeriodKey,
      meal_period_key: mapping.mealPeriodKey,
      meal_period_index: mapping.index,
      meal_period_name: mapping.name,
      account_id: mapping.accountId,
      account_description: mapping.accountDescription,
      class_department_location: mapping.classDepartmentLocation,
      qb_customer_id: mapping.customerId,
      customer_id: mapping.customerId,
    }))

  const handleUpdate = (dimensionMappingOverride) => {
    const d365DimensionsValue =
      typeof dimensionMappingOverride === "string"
        ? dimensionMappingOverride
        : currentDimensionMapping || ""
    const primaryMealMapping = isMealPeriodMode
      ? mealMappings.find((mapping) => mapping.index === 1)
      : null
    const taxAccountId = primaryMealMapping?.accountId ?? inputBox1
    const taxAccountName = primaryMealMapping?.accountDescription ?? inputBox2
    const taxClass = primaryMealMapping?.classDepartmentLocation ?? inputBox3
    const taxCustomer = primaryMealMapping?.customerId ?? inputBox4
    const qbCustomerValue = (inputBox4 ?? "").toString().trim() || null
    const internalCustomerId = (customerId ?? "").toString().trim() || null

    if (database.currentCompany.type === "RETAIL") {
      dispatch(
        databaseActions.updateRetailTaxes(
          userState.user.user_id,
          item.company_id,
          database.currentShop.shop_id,
          inputBox1,
          inputBox2,
          item.tax_id,
          inputBox3,
          qbCustomerValue,
          internalCustomerId,
          d365DimensionsValue,
          inputBox5,
          databaseActions.getShopTaxes(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      )
    }

    if (database.currentCompany.type === "RETAILX") {
      dispatch(
        databaseActions.updateRetailTaxesX(
          userState.user.user_id,
          item.company_id,
          database.currentShop.shop_id,
          inputBox1,
          inputBox2,
          item.tax_id,
          inputBox3,
          qbCustomerValue,
          internalCustomerId,
          databaseActions.getShopTaxesX(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      )
    }

    if (database.currentCompany.type === "RESTOK") {
      if (database.currentCompany.direct) {
        dispatch(
          databaseActions.updateRestoTaxesK(
            userState.user.user_id,
            item.company_id,
            item.tax_id,
            taxAccountId,
            taxAccountName,
            0,
            taxClass,
            (taxCustomer ?? "").toString().trim() || null,
            (taxCustomer ?? "").toString().trim() || null,
            isMealPeriodMode ? getMealMappingsPayload() : null,
            d365DimensionsValue,
            databaseActions.getAreaTaxesK(
              userState.user.user_id,
              database.currentCompany.id,
              0
            )
          )
        )
      } else {
        dispatch(
          databaseActions.updateRestoTaxesK(
            userState.user.user_id,
            item.company_id,
            item.tax_id,
            taxAccountId,
            taxAccountName,
            database.currentShop.area_id,
            taxClass,
            (taxCustomer ?? "").toString().trim() || null,
            (taxCustomer ?? "").toString().trim() || null,
            isMealPeriodMode ? getMealMappingsPayload() : null,
            d365DimensionsValue,
            databaseActions.getAreaTaxesK(
              userState.user.user_id,
              database.currentCompany.id,
              database.currentShop.area_id
            )
          )
        )
      }
    }

    if (database.currentCompany.type === "RESTO") {
      if (database.currentCompany.direct) {
        dispatch(
          databaseActions.updateRestoTaxes(
            userState.user.user_id,
            item.company_id,
            item.tax_id,
            inputBox1,
            inputBox2,
            0,
            inputBox3,
            qbCustomerValue,
            internalCustomerId,
            databaseActions.getAreaTaxes(
              userState.user.user_id,
              database.currentCompany.id,
              0
            )
          )
        )
      } else {
        dispatch(
          databaseActions.updateRestoTaxes(
            userState.user.user_id,
            item.company_id,
            item.tax_id,
            inputBox1,
            inputBox2,
            database.currentShop.area_id,
            inputBox3,
            qbCustomerValue,
            internalCustomerId,
            databaseActions.getAreaTaxes(
              userState.user.user_id,
              database.currentCompany.id,
              database.currentShop.area_id
            )
          )
        )
      }
    }

    setEdit(false)
  }

  const filterBy = (list, term) => {
    const t = (term || "").toLowerCase()
    return (list || []).filter((x) => {
      const id = (x?.id ?? "").toString().toLowerCase()
      const desc = (x?.description ?? "").toString().toLowerCase()
      return id.includes(t) || desc.includes(t)
    })
  }

  const filteredAccounts = filterBy(accountsList, searchTextAcc)


  const filteredCustomers = filterBy(customersList, customerSearchText)
  const d365DimensionRows = isD365Integration
    ? (d365DimensionsList || []).filter((cls) => cls.dimension_level)
    : []
  const currentDimensionMapping = dimensionMapping || normalDimensionMapping
  const settingsButtonStyle = {
    marginLeft: 6,
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    background: "#fff",
    color: "#0f172a",
    cursor: "pointer",
    fontSize: 12,
    padding: "3px 6px",
  }
  const renderD365SettingsButton = (target) =>
    isD365Integration ? (
      <button
        type="button"
        style={settingsButtonStyle}
        title={formatD365DimensionTooltip(currentDimensionMapping) || "No dimensions mapped"}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setD365Modal(target)
        }}
      >
        {hasD365DimensionMapping(currentDimensionMapping) ? "Dimensions*" : "Dimensions"}
      </button>
    ) : null
  const applyD365DimensionValue = (nextValue) => {
    if (d365Modal && typeof d365Modal === "object") {
      updateMealMapping(d365Modal.index, { classDepartmentLocation: nextValue })
    } else {
      handleUpdate(nextValue)
    } 
  }
  const handleTaxClassAllocationChange = (value) => {
    if (/^\d*(\.\d*)?$/.test(value)) {
      setInputBox5(value)
    }
  }
  const d365ModalElement = (
    <D365DimensionsModal
      open={!!d365Modal}
      value={currentDimensionMapping}
      dimensions={d365DimensionRows}
      onClose={() => setD365Modal(null)}
      onApply={applyD365DimensionValue}
    />
  )

  if (isMealPeriodMode) {
    const rows = mealMappings.length > 0 ? mealMappings : buildMealMappings()

    if (!edit) {
      return (
        <>
          {d365ModalElement}
          {rows.map((mapping, index) => (
            <Tr
              key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
              textAlign="right"
              className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
              onDoubleClick={() => setEdit(true)}
            >
              {index === 0 && (
                <>
                  <Td width="12%" className="td-pos" rowSpan={rows.length}>
                    {item.company_id}
                  </Td>
                  <Td width="10%" className="td-pos" rowSpan={rows.length}>
                    {item.tax_id}
                  </Td>
                  <Td width="18%" className="td-pos td-pos-last" rowSpan={rows.length}>
                    {item.tax_name}
                  </Td>
                </>
              )}

              <Td width="15%" className="td-acc td-acc-first">
                <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr", alignItems: "center" }}>
                  <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mapping.name}
                  </strong>
                  <span>{mapping.accountId}</span>
                </div>
              </Td>
              <Td width="20%" className="td-acc">{mapping.accountDescription}</Td>
              <Td width="10%" className="td-acc" style={{ display: hideClassDepartmentColumn ? "none" : undefined }}>
                {mapping.classDepartmentLocation}
                {index === 0 && renderD365SettingsButton("main")}
              </Td>
              <Td width="10%" className="td-acc">{mapping.customerId}</Td>

              {index === 0 && (
                <Td width="5%" textAlign="right" className="td-acc" rowSpan={rows.length}>
                  <Button onClick={() => setEdit(true)} size="small" fill="true">
                    Edit
                  </Button>
                </Td>
              )}
            </Tr>
          ))}
        </>
      )
    }

    return (
      <>
        {d365ModalElement}
        {rows.map((mapping, index) => (
          <Tr
            key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
            textAlign="right"
            className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
            onDoubleClick={() => setEdit(true)}
          >
            {index === 0 && (
              <>
                <Td width="12%" className="td-pos" rowSpan={rows.length}>
                  {item.company_id}
                </Td>

                <Td width="10%" className="td-pos" rowSpan={rows.length}>
                  {item.tax_id}
                </Td>

                <Td width="18%" className="td-pos td-pos-last" rowSpan={rows.length}>
                  {item.tax_name}
                </Td>
              </>
            )}

            <Td width="15%" className="td-acc td-acc-first">
              <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr", alignItems: "center" }}>
                <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {mapping.name}
                </strong>
                <Input
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.accountId}
                  onChange={(e) =>
                    updateMealMapping(mapping.index, { accountId: e.target.value })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                />
              </div>
            </Td>

            <Td width="20%" className="td-acc">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.accountDescription}
                onChange={(e) =>
                  updateMealMapping(mapping.index, {
                    accountDescription: e.target.value,
                  })
                }
                onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
              />
            </Td>

            <Td width="10%" className="td-acc" style={{ display: hideClassDepartmentColumn ? "none" : undefined }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Input
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.classDepartmentLocation}
                  onChange={(e) =>
                    updateMealMapping(mapping.index, {
                      classDepartmentLocation: e.target.value,
                    })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                />
                {index === 0 && renderD365SettingsButton("main")}
              </div>
            </Td>

            <Td width="10%" className="td-acc">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.customerId}
                onChange={(e) =>
                  updateMealMapping(mapping.index, { customerId: e.target.value })
                }
                onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
              />
            </Td>

            {index === 0 && (
              <Td width="5%" textAlign="right" className="td-acc" rowSpan={rows.length}>
                {!edit ? (
                  <Button onClick={() => setEdit(true)} size="small" fill="true">
                    Edit
                  </Button>
                ) : (
                  <Button onClick={handleUpdate} size="small" fill="true" disabled={!changed}>
                    Update
                  </Button>
                )}
              </Td>
            )}
          </Tr>
        ))}
      </>
    )
  }

  return (
    <>
    {d365ModalElement}
    <Tr
      textAlign="right"
      className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
      onDoubleClick={() => {
        setInputBox1(normalAccount)
        setInputBox2(normalAccountDescription)
        setInputBox3(normalClassDeptLoc)
        setInputBox4(normalCustomer)
        setCustomerId(normalCustomerInternalId)
        setInputBox5(normalTaxClassAllocation)
        setMealMappings(buildMealMappings())
        setEdit(true)
      }}
    >
      <Td width={isRetailR ? "10%" : "12%"} className="td-pos">
        {item.company_id}
      </Td>

      <Td width={isRetailR ? "8%" : "10%"} className="td-pos">
        {item.tax_id}
      </Td>

      <Td width={isRetailR ? "14%" : "18%"} className="td-pos td-pos-last">
        {item.tax_name}
      </Td>

      {/* Account Id + dropdown */}
      <Td width={isRetailR ? "13%" : "15%"} className="td-acc td-acc-first" style={{ position: "relative" }}>
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox1}
            <div className="flex" />
          </div>
        ) : (
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox1}
              onChange={(e) => setInputBox1(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
            />
            <span
              style={{ marginLeft: 4, cursor: "pointer", fontSize: 16, userSelect: "none" }}
              onClick={() => {
                setShowDropdownAcc((p) => !p)
                setSearchTextAcc("")
              }}
            >
              ⏷
            </span>

            {showDropdownAcc && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#fff",
                  zIndex: 1000,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  width: 450,
                  minWidth: "100%",
                  maxHeight: 200,
                  overflowY: "auto",
                  marginTop: 4,
                }}
              >
                <div style={{ padding: 4 }}>
                  <input
                    ref={accSearchRef}
                    type="text"
                    placeholder="Search..."
                    value={searchTextAcc}
                    onChange={(e) => setSearchTextAcc(e.target.value)}
                    style={{
                      width: "100%",
                      padding: 6,
                      fontSize: 14,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const first = filteredAccounts[0]
                        if (first) {
                          setInputBox1(first.id)
                          setInputBox2(first.description)
                          setShowDropdownAcc(false)
                          setSearchTextAcc("")
                        }
                      }
                    }}
                  />
                </div>

                {filteredAccounts.map((acc) => (
                  <div
                    key={acc.id}
                    style={{ padding: "6px 8px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                    onClick={() => {
                      setInputBox1(acc.id)
                      setInputBox2(acc.description)
                      setShowDropdownAcc(false)
                      setSearchTextAcc("")
                    }}
                  >
                    <strong>{acc.id}</strong> — {acc.description}
                  </div>
                ))}

                {filteredAccounts.length === 0 && (
                  <div style={{ padding: "6px 8px", color: "#999" }}>No results</div>
                )}
              </div>
            )}
          </div>
        )}
      </Td>

      {/* Account Description */}
      <Td width={isRetailR ? "17%" : "20%"} className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox2}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper">
            <div>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox2}
                onChange={(e) => setInputBox2(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
              />
            </div>
          </div>
        )}
      </Td>

      {/* Class/Dep/Loc */}
      <Td width={isRetailR ? "9%" : "10%"} className="td-acc" style={{ position: "relative", display: hideClassDepartmentColumn ? "none" : undefined }}>
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox3}
            {renderD365SettingsButton("main")}
            <div className="flex" />
          </div>
        ) : (
          <QuickBooksClassDepartmentField
            value={inputBox3}
            onChange={(value) => {
              setInputBox3(value)
              setChanged(true)
            }}
            classDepartments={classList}
            onEnter={handleUpdate}
          >
            {renderD365SettingsButton("main")}
          </QuickBooksClassDepartmentField>
        )}
      </Td>

      {/* Customer */}
      <Td width={isRetailR ? "9%" : "10%"} className="td-acc" style={{ position: "relative" }}>
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox4}
            <div className="flex" />
          </div>
        ) : (
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox4}
              onChange={(e) => setInputBox4(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
            />
            <span
              style={{ marginLeft: 4, cursor: "pointer", fontSize: 16, userSelect: "none" }}
              onClick={() => {
                setShowCustomerDropdown((p) => !p)
                setCustomerSearchText("")
              }}
            >
              ⏷
            </span>

            {showCustomerDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#fff",
                  zIndex: 1000,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  width: 450,
                  minWidth: "100%",
                  maxHeight: 200,
                  overflowY: "auto",
                  marginTop: 4,
                }}
              >
                <div style={{ padding: 4 }}>
                  <input
                    ref={customerSearchRef}
                    type="text"
                    placeholder="Search..."
                    value={customerSearchText}
                    onChange={(e) => setCustomerSearchText(e.target.value)}
                    style={{
                      width: "100%",
                      padding: 6,
                      fontSize: 14,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const first = filteredCustomers[0]
                        if (first) {
                          setInputBox4(String(first.id))
                          setCustomerId(String(first.id))
                          setShowCustomerDropdown(false)
                          setCustomerSearchText("")
                        }
                      }
                    }}
                  />
                </div>

                {filteredCustomers.map((c) => (
                  <div
                    key={c.id}
                    style={{ padding: "6px 8px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                    onClick={() => {
                      setInputBox4(String(c.id))
                      setCustomerId(String(c.id))
                      setShowCustomerDropdown(false)
                      setCustomerSearchText("")
                    }}
                  >
                    <strong>{c.id}</strong> — {c.description}
                  </div>
                ))}

                {filteredCustomers.length === 0 && (
                  <div style={{ padding: "6px 8px", color: "#999" }}>No results</div>
                )}
              </div>
            )}
          </div>
        )}
      </Td>

      {isRetailR && (
        <Td width="15%" className="td-acc">
          {!edit ? (
            <div className="row-input-wrapper">
              {inputBox5}
              <div className="flex" />
            </div>
          ) : (
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              inputMode="decimal"
              size="small"
              value={inputBox5}
              onChange={(e) => handleTaxClassAllocationChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
            />
          )}
        </Td>
      )}

      {/* Action */}
      <Td width="5%" textAlign="right" className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper-end">
            <Button
              onClick={() => {
                setInputBox1(normalAccount)
                setInputBox2(normalAccountDescription)
                setInputBox3(normalClassDeptLoc)
                setInputBox4(normalCustomer)
                setCustomerId(normalCustomerInternalId)
                setInputBox5(normalTaxClassAllocation)
                setMealMappings(buildMealMappings())
                setEdit(true)
              }}
              size="small"
              fill="true"
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="row-input-wrapper-end">
            {changed ? (
              <Button onClick={handleUpdate} size="small" fill="true">
                Update
              </Button>
            ) : inputBox1 === "" &&
              inputBox2 === "" &&
              inputBox3 === "" &&
              inputBox4 === "" &&
              (!isRetailR || inputBox5 === "") ? null : (
              <Button
                onClick={() => {
                  setInputBox1(normalAccount)
                  setInputBox2(normalAccountDescription)
                  setInputBox3(normalClassDeptLoc)
                  setInputBox4(normalCustomer)
                  setCustomerId(normalCustomerInternalId)
                  setInputBox5(normalTaxClassAllocation)
                  setMealMappings(buildMealMappings())
                  setEdit(false)
                }}
                size="small"
                fill="true"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </Td>
    </Tr>
    </>
  )
}

export default Row